import { useTranslation } from "react-i18next";
import { User } from "../../services/user/userModel";
import { useMemo } from "react";
import {
  Invitation,
  Organization,
} from "../../services/organization/organizationModel";
import { Link } from "react-router-dom";

const UsersOverview = ({
  organization,
  users,
  invitations,
  onNewUser,
}: {
  organization: Organization;
  users: User[];
  invitations: Invitation[];
  onNewUser?: () => void;
}) => {
  const { t } = useTranslation(["organization"]);

  const nonDisabledUsers = useMemo(
    () => users.filter((u) => u.organizationMembershipStatus === "active"),
    [users],
  );

  const adminsCount = useMemo(
    () => nonDisabledUsers.reduce((acc, u) => (u.adminOrga ? acc + 1 : acc), 0),
    [nonDisabledUsers],
  );

  const guestsCount = useMemo(
    () =>
      invitations
        .filter((i) => !i.disabledDate)
        .reduce(
          (acc, i) =>
            i.membershipType === "GUEST" && i.userId ? acc + 1 : acc,
          0,
        ),
    [invitations],
  );

  const pendingCount = useMemo(
    () =>
      invitations
        .filter((i) => !i.disabledDate)
        .reduce((acc, i) => (i.userId == null ? acc + 1 : acc), 0),
    [invitations],
  );

  const seatsLeft = useMemo(() => {
    return Math.max(
      organization.maxMembersCount -
        (nonDisabledUsers.length + guestsCount + pendingCount),
      0,
    );
  }, [
    organization.maxMembersCount,
    nonDisabledUsers.length,
    guestsCount,
    pendingCount,
  ]);

  return (
    <div className="admin-kpis">
      <div className="ui-kpi admin-kpis_lead">
        <span className="ui-kpi_l">
          {t("organization:users.AVAILABLE_SEATS")}
        </span>
        <span className="ui-kpi_v">{seatsLeft}</span>
        {onNewUser &&
          (seatsLeft > 0 ? (
            <div className="admin-kpis_cta">
              <button className="btn--white --btn--s" onClick={onNewUser}>
                {t("organization:users.NEW_USER")}
              </button>
            </div>
          ) : (
            <>
              <div className="admin-kpis_note">
                {t("organization:users.NO_SEAT_LEFT")}
              </div>
              <div className="admin-kpis_cta">
                <Link
                  to={`/organization/contact?object=${encodeURIComponent(
                    t("organization:users.NEED_MORE_SEAT_OBJECT"),
                  )}`}
                  className="btn--white --btn--s"
                >
                  {t("organization:users.CONTACT_2MM")}
                </Link>
              </div>
            </>
          ))}
      </div>

      <div className="ui-kpi">
        <span className="ui-kpi_l">
          {t("organization:users-labels.ADMINS")}
        </span>
        <span className="ui-kpi_v">{adminsCount}</span>
      </div>
      <div className="ui-kpi">
        <span className="ui-kpi_l">
          {t("organization:users-labels.MEMBERS")}
        </span>
        <span className="ui-kpi_v">
          {nonDisabledUsers.length - adminsCount}
        </span>
      </div>
      <div className="ui-kpi">
        <span className="ui-kpi_l">
          {t("organization:users-labels.GUESTS")}
        </span>
        <span className="ui-kpi_v">{guestsCount}</span>
      </div>
      <div className="ui-kpi">
        <span className="ui-kpi_l">
          {t("organization:users-labels.PENDING")}
        </span>
        <span className="ui-kpi_v">{pendingCount}</span>
      </div>
    </div>
  );
};

export default UsersOverview;
